/**
 * Inline onboarding progress tracker for the hamburger menu.
 * 4 steps: Save → Try On → Follow → Safari Extension
 * Lightweight, gamified, celebratory.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Heart, Shirt, UserPlus, Compass, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

interface Step {
  id: string;
  label: string;
  icon: LucideIcon;
  completed: boolean;
}

interface OnboardingProgressProps {
  onNavigate: (path: string) => void;
  onOpenFindUsers: () => void;
  onCloseMenu: () => void;
}

function useOnboardingSteps() {
  const { user } = useAuth();

  const { data: steps = [], isLoading } = useQuery({
    queryKey: ['onboarding-progress', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const [savesRes, tryOnsRes, followsRes, profileRes] = await Promise.all([
        supabase.from('saved_items').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('tryon_jobs').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('follows').select('id', { count: 'exact', head: true }).eq('follower_id', user.id),
        supabase.from('profiles').select('safari_extension_enabled').eq('user_id', user.id).single(),
      ]);

      return [
        { id: 'save', label: 'Save an item', icon: Heart, completed: (savesRes.count ?? 0) > 0 },
        { id: 'tryon', label: 'Try something on', icon: Shirt, completed: (tryOnsRes.count ?? 0) > 0 },
        { id: 'follow', label: 'Follow a friend', icon: UserPlus, completed: (followsRes.count ?? 0) > 0 },
        { id: 'extension', label: 'Try Safari extension', icon: Compass, completed: !!(profileRes.data as any)?.safari_extension_enabled },
      ] as Step[];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  return { steps, isLoading };
}

export function OnboardingProgress({ onNavigate, onOpenFindUsers, onCloseMenu }: OnboardingProgressProps) {
  const { steps, isLoading } = useOnboardingSteps();
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('want_onboarding_progress_dismissed') === 'true'
  );

  if (isLoading || dismissed) return null;

  const completedCount = steps.filter(s => s.completed).length;
  const allDone = completedCount === steps.length;

  // Auto-hide when all done (after a brief celebration window)
  if (allDone) return null;

  const handleStepClick = (step: Step) => {
    if (step.completed) return;
    switch (step.id) {
      case 'save': onNavigate('/saves'); break;
      case 'tryon': onNavigate('/tryon'); break;
      case 'follow': onCloseMenu(); onOpenFindUsers(); break;
      case 'extension': onNavigate('/safari-extension'); break;
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('want_onboarding_progress_dismissed', 'true');
    setDismissed(true);
  };

  return (
    <div className="mx-3 mt-4 mb-1">
      <div className="rounded-2xl bg-muted/50 border border-border/40 p-3.5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Your Journey</p>
            <span className="text-[10px] font-bold text-primary bg-primary/10 rounded-full px-1.5 py-0.5">
              {completedCount}/{steps.length}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-border/50 rounded-full mb-3.5 overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${(completedCount / steps.length) * 100}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>

        {/* Steps */}
        <div className="space-y-1">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(step)}
                disabled={step.completed}
                className={`flex items-center gap-3 w-full px-2.5 py-2 rounded-xl text-left transition-all duration-200 ${
                  step.completed
                    ? 'opacity-60'
                    : 'hover:bg-background active:scale-[0.98]'
                }`}
              >
                {/* Step indicator */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  step.completed
                    ? 'bg-primary/15'
                    : 'bg-background border border-border/60'
                }`}>
                  <AnimatePresence mode="wait">
                    {step.completed ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                      >
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </motion.div>
                    ) : (
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    )}
                  </AnimatePresence>
                </div>
                <span className={`text-[13px] ${
                  step.completed
                    ? 'text-muted-foreground line-through decoration-muted-foreground/40'
                    : 'text-foreground font-medium'
                }`}>
                  {step.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
